/**
 * useSocket.ts  -  Socket.io real-time event handling for the teacher portal.
 *
 * Connects to the API server's Socket.io endpoint on mount (when authenticated).
 * Joins the teacher's personal room so the server can push targeted events.
 *
 * Events handled:
 *   submission:new       -  new student submission; invalidates submissions + dashboard
 *   submission:scored    -  submission scored (e.g. by co-teacher); invalidates submissions
 *   class:join-request   -  student requested to join a class; invalidates join requests
 *   message:new          -  parent replied to a thread; invalidates messages + unread count
 */

import { useEffect, useRef } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'

// In production, VITE_API_BASE_URL is unset so we use window.location.origin
// which is the nginx host  -  nginx proxies /socket.io/ to the server container.
const SOCKET_URL = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '')
  ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:4000')

export function useSocket() {
  const qc      = useQueryClient()
  const user    = useAuthStore((s) => s.user)
  const token   = useAuthStore((s) => s.accessToken)
  const sockRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!user?.id || !token) return

    const socket = io(SOCKET_URL, {
      auth:              { token },
      transports:        ['websocket', 'polling'],
      reconnectionDelay: 2000,
    })

    sockRef.current = socket

    socket.on('connect', () => {
      socket.emit('join:teacher', user.id)
    })

    socket.on('submission:new', () => {
      void qc.invalidateQueries({ queryKey: ['submissions'] })
      void qc.invalidateQueries({ queryKey: ['analytics', 'dashboard'] })
    })

    socket.on('submission:scored', () => {
      void qc.invalidateQueries({ queryKey: ['submissions'] })
    })

    socket.on('class:join-request', ({ classId }: { classId: string }) => {
      void qc.invalidateQueries({ queryKey: ['classes', classId, 'join-requests'] })
      void qc.invalidateQueries({ queryKey: ['classes'] })
      void qc.invalidateQueries({ queryKey: ['analytics', 'dashboard'] })
    })

    socket.on('message:new', ({ threadId }: { threadId: string }) => {
      void qc.invalidateQueries({ queryKey: ['messages', 'unread'] })
      void qc.invalidateQueries({ queryKey: ['messages', 'threads'] })
      void qc.invalidateQueries({ queryKey: ['messages', 'thread', threadId] })
    })

    return () => {
      socket.disconnect()
      sockRef.current = null
    }
  }, [user?.id, token]) // eslint-disable-line react-hooks/exhaustive-deps

  return sockRef
}
